import { CommunityStatus, EventJourneyStatus, Prisma } from "@prisma/client";
import { z } from "zod";
import { ATTENDANCE_STATUSES } from "~/utils/enums";

export type AddEvent = z.input<typeof addEventsInputValidator>;
export const addEventsInputValidator = z.object({
    name: z.string(),
    description: z.string().nullish(),
    startTime: z.string(),
    endTime: z.string(),
    communitySlug: z.string(),
    tags: z.array(z.string()),
    isTemplate: z.boolean().optional(),
    isMicroAutoApproved: z.boolean().optional(),
});
export type CloneInput = z.input<typeof cloneEventInputValidator>;
export const cloneEventInputValidator = z.object({
    communitySlug: z.string(),
    eventId: z.string(),
    isSimulcast: z.boolean(),
    tags: z.array(z.string()),
    resourceTags: z.array(z.string()).default([]),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
    locations: z
        .array(
            z.object({
                city: z.string(),
            }),
        )
        .optional(),
    isTemplate: z.boolean().optional(),
    isMicroAutoApproved: z.boolean().optional(),
    currentUserIsHost: z.boolean().optional(),
    description: z.string().optional(),
});

// https://chat.whatsapp.com/GO8F85FcdycDz8maITFnv6
const whatsappSchema = z.string().refine(
    (handle) => {
        return /^(https?:\/\/)?chat\.whatsapp\.com\/(?:invite\/)?([a-zA-Z0-9_-]{22})$/.test(
            handle,
        );
    },
    { message: "Invalid Whatsapp group chat invite format" },
);
// https://t.me/xxxxasd
const telegramSchema = z.string().refine(
    (handle) => {
        return /^(?:|(https?:\/\/)?(|www)[.]?((t|telegram)\.me)(\/joinchat)?\/)[a-zA-Z0-9_]{5,32}$/.test(
            handle,
        );
    },
    { message: "Invalid Whatsapp group chat invite format" },
);
// https://www.linkedin.com/in/bernii/
export const linkedInSchema = z
    .string()
    .refine(
        (handle) =>
            /^(?:|(https?:\/\/)?(|www)[.]?((linkedin)\.com)(\/in)?\/)[a-zA-Z0-9_/]{5,32}$/.test(
                handle,
            ),
        {
            message:
                "Please enter a valid LinkedIn profile URL (e.g. https://www.linkedin.com/in/username)",
        },
    );

export const urlSchema = z
    .string()
    .refine(
        (val) =>
            /^(https?:\/\/)?(?:www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?::\d+)?(?:\/[^\s]*)?$/.test(
                val,
            ),
        {
            message: "Invalid URL",
        },
    );

export const groupChatUrlValidator = whatsappSchema
    .or(telegramSchema)
    .or(z.string().url());
export const getValidGroupChatType = (url: string) => {
    if (whatsappSchema.safeParse(url).success) return "whatsapp";
    if (telegramSchema.safeParse(url).success) return "telegram";
    return "other";
};

export type UpdateEventSettings = z.input<
    typeof updateEventSettingsInputValidator
>;
export const updateEventSettingsInputValidator = z.object({
    id: z.string().optional(),
    communitySlug: z.string().optional(),
    isNewSlug: z.boolean().optional(),
    slug: z.string(),
    isPreapproved: z.boolean().optional(),
    private: z.boolean().optional(),
    isMembersOnly: z.boolean().optional(),
    isMembersOnlyTagGated: z.boolean().optional(),
    isPaidAllowed: z.boolean().optional(),
    isApplyToHostAllowed: z.boolean().optional(),
    isApplyToSponsorAllowed: z.boolean().optional(),
    isMultipleTicketsAllowed: z.boolean().optional(),
    nftTokenGateCollectionId: z.string().nullable().optional(),
    protectedTiemout: z.number().nullable(),
    protectedFields: z
        .array(z.object({ name: z.string(), value: z.string().optional() }))
        .optional(),
    questionnairesRequired: z.array(z.object({ id: z.string() })).optional(),
    eventJourneyStatus: z
        .enum(
            Object.values(EventJourneyStatus) as [
                keyof typeof EventJourneyStatus,
            ],
        )
        .optional(),
});

export const locationDataValidator = z.object({
    timezone: z.string(),
    city: z.string().nullable().optional(),
    longitude: z.number().nullable().optional(),
    latitude: z.number().nullable().optional(),

    googleMapsPlaceId: z.string().nullable().optional(),
    googleMapsPlaceName: z.string().nullable().optional(),
    googleMapsPlaceTypes: z.array(z.string()).nullable().optional(),
    location: z.string().optional(),
    locationName: z.string().nullable().optional(),
    useGoogleMapsLocation: z.boolean().optional(),
    country: z.string().nullable().optional(),
});

export type UpsertEvent = z.input<typeof upsertEventInputValidator>;
export const upsertEventInputValidator = z.object({
    // Required Properties
    slug: z.string(),
    name: z.string().max(80),
    nameLocked: z.string().max(80).nullable().optional(),
    hosts: z.string().array(),
    attendees: z.array(
        z.object({
            userId: z.string(),
            status: z.enum([...ATTENDANCE_STATUSES] as [string, ...string[]]),
        }),
    ),
    isTimeSet: z.boolean(),
    startTime: z.string(),
    startTimeLocked: z.string().nullable().optional(),
    timezone: z.string(),
    eventJourneyStatus: z.enum([
        "DRAFT",
        "PUBLISHED",
        "ARCHIVED",
        "DELETED",
        "DRAFT_PROPOSED",
        "PROPOSED",
        "IGNORED",
    ]),
    locationLocked: locationDataValidator.optional().nullable(),

    // chapter member related properties
    membersOnlyRSVPPeriodnHours: z.number().optional(),

    // Nullable Properties
    description: z.string().nullable(),
    descriptionLocked: z.string().nullable().optional(),
    endTime: z.string().nullable(),
    endTimeLocked: z.string().nullable().optional(),
    primaryHostId: z.string().nullable(),
    image: z.string().nullable(),
    banner: z.string().nullable(),
    tags: z.array(z.string()).nullable(),
    resourceTags: z.array(z.string()).nullable(),
    priceDollars: z.number().nullable(),
    isPaidAllowed: z.boolean().nullable(),
    currencyCode: z.string().optional(),
    capacity: z.number().nullable(),
    protectedTiemout: z.number().nullish(),
    priceCents: z.number().nullable(),
    city: z.string().nullable(),

    // Nullish properties
    communityId: z
        .string()
        .nullish()
        .transform((c) => (c === "" ? undefined : c)),
    locationName: z.string().nullish(),
    googleMapsLink: z.string().nullish(),
    googleMapsPlaceId: z.string().nullish(),
    googleMapsPlaceName: z.string().nullish(),
    country: z.string().nullish(),

    // Optional Properties
    id: z.string().optional(),
    newSlug: z.string().optional(),
    isPreapproved: z.boolean().optional(),
    location: z.string().default(""),
    googleMapsPlaceTypes: z.array(z.string()).optional(),
    sponsors: z.array(z.string()).nullish(),
    protectedFields: z
        .array(z.object({ name: z.string(), value: z.string().optional() }))
        .nullish(),
    longitude: z.number().nullish(),
    latitude: z.number().nullish(),
    isVirtual: z.boolean().nullish(),
    virtualEventUrl: z.string().nullish(),
    groupChatUrl: groupChatUrlValidator.nullish(),

    isExternalEvent: z.boolean().nullish(),
    externalEventUrl: z.string().url().or(z.literal("")).nullish(),

    // Default Properties
    useGoogleMapsLocation: z.boolean().default(false),
    private: z.boolean().default(false),

    // template options
    isTemplate: z.boolean().optional(),
    isMicroAutoApproved: z.boolean().optional(),
    showNotes: z.string().optional(),
    commManagerNotes: z.string().optional(),
});

export const communityStatusValues = Object.values(CommunityStatus) as [
    string,
    ...string[],
];
export const communityStatusEnum = z.enum(communityStatusValues);
export const communityFilter = z
    .object({
        id: z.string().optional(),
        name: z.string().optional(),
        slug: z.string().optional(),
        status: communityStatusEnum.optional(),
    })
    .optional();

export const eventJourneyStatusValues = Object.values(EventJourneyStatus) as [
    string,
    ...string[],
];
export const eventJourneyStatusEnum = z.enum(eventJourneyStatusValues);
export const eventsGetAllInputValidator = z
    .object({
        slug: z.string().optional(),
        draftOnly: z.boolean().optional().default(false),
        searchTerm: z.string().optional(),
        includeAttendeesNames: z.boolean().default(true),
        includeHostNames: z.boolean().default(true),
        pastEvents: z.boolean().nullable().default(true),
        geoLocation: z
            .object({ latitude: z.number(), longitude: z.number() })
            .optional(),
        dateFilter: z.date().optional(),
        communityFilter: communityFilter,
        communitySlugs: z.array(z.string()).optional(),
        onlyShowCurrentUserAttendingEvents: z
            .boolean()
            .optional()
            .default(true),
        onlyShowCurrentUserAttendingAndWaitingEvents: z
            .boolean()
            .optional()
            .default(false),
        onlyShowCurrentUserHostingEvents: z.boolean().optional().default(false),
        startOfCurrentDay: z.date().optional(),
        currentTime: z.date().optional(),
        timezone: z.string().optional(),
        take: z.number().max(300).optional(), //limit
        skip: z.number().optional(), // offset
        eventJourneyStatus: z
            .union([eventJourneyStatusEnum, z.array(eventJourneyStatusEnum)])
            .optional(),
        upcomingEvents: z.boolean().optional(),
        displayPinnedEvents: z.boolean().optional().default(false),
        userId: z.string().optional(),
        showAdditionalHostUserInfo: z.boolean().optional().default(false),
        showSystemMessages: z.boolean().optional().default(false),
        useDateSortOverRide: z.boolean().optional().default(false),
        country: z.string().optional(),
        eventSlug: z.string().optional(),
        eventSlugRouter: z.string().optional().nullable(),
        // single filter option
        tag: z.string().optional(),
        privateOnlyAndHosting: z.boolean().optional(),
        dateRangeFilters: z
            .object({
                startDate: z.date(),
                endDate: z.date(),
            })
            .optional(),
        // find all events with any of these tags
        tags: z.array(z.string()).optional(),
        resourceTags: z.array(z.string()).optional(),
        excludeResourceTags: z.array(z.string()).optional(),

        sadgeFilterOn: z.boolean().optional().default(false),
        getProfileEvents: z.boolean().optional(),
    })
    .optional();

export const eventsGetBatchInputValidator = z.object({
    filters: eventsGetAllInputValidator,
    pagination: z.object({
        limit: z.number(),
        cursor: z.string().nullish(),
        skip: z.number().optional(),
    }),
});
export type GetBatchQueryInput = z.input<typeof eventsGetBatchInputValidator>;

export type GetAllEventsInput = z.input<typeof eventsGetAllInputValidator>;

export type EventWithHosts = Prisma.EventGetPayload<{
    include: { hosts: { include: { user: true } } };
}>;
export type EventWithHostsCommunity = Prisma.EventGetPayload<{
    include: {
        hosts: { include: { user: true } };
        community: {
            select: { name: true; slug: true; id: true; image: true };
        };
        sponsorsDetails: true;
    };
}>;
export type EventWithMinimalHosts = Pick<
    Prisma.EventGetPayload<{
        select: {
            hosts: { select: { user: { select: { id: true } } } };
            communityId: true;
        };
    }>,
    "hosts" | "communityId"
>;
export type EventBadges = Prisma.BadgeGetPayload<{
    select: { id: true; path: true; description: true };
}>[];

export interface IProtectedField {
    name: string;
    value?: string;
}

export type PastFutureEventsFilter = {
    val:
        | {
              gte: Date;
              lte?: undefined;
          }
        | {
              lte: Date;
              gte?: undefined;
          };
};

export type UpdateProposedEventStatus = z.input<
    typeof updateProposedEventStatus
>;
export const updateProposedEventStatus = z.object({
    eventId: z.string(),
    eventJourneyStatus: eventJourneyStatusEnum,
    reason: z.string().optional(),
});
export type ProtectedFields = {
    name: string;
    value: string;
}[];

export const toTime24 = (input: Date) =>
    Intl.DateTimeFormat("en-US", {
        timeZone: "UTC",
        timeStyle: "short",
        hour12: false,
    }).format(input);

// the keys the must exist on the input
type RequiredEventProperties = {
    id: string;
    slug: string;
    name: string;
    startTime: Date;
    city: string | null;
    googleMapsLink: string | null;
    description: string | null;
    tags: string[];
    image: string | null;
    country: string | null;
};

export type FlexibleEventType = Partial<RequiredEventProperties> &
    Record<string, unknown>;

export type EventImageType = Prisma.EventGetPayload<{
    select: {
        id: true;
        imageOverlay: {
            select: {
                brandAssetUrl: true;
                textsOverlay: true;
            };
        };
        resourceTags: true;
        city: true;
        image: true;
        imageBlurhash: true;
    };
}> & {
    // backwards compatibility
    community: {
        slug: string;
        membershipTiers?: {
            name?: string;
            tags?: string[];
        }[];
    } | null;
} & {
    // for reusability with profile events
    user?: {
        id: string;
    } | null;
};

export type EventListItem = EventImageType & {
    name: string | null;
    slug: string;
    startTime: Date;
    endTime?: Date | null;
    location: string | null;
    capacity: number | null;
    tags: string[];
    resourceTags: string[];
    city: string | null;
    isTimeSet: boolean | null;
    virtualEventUrl: string | null;
    hosts: {
        user: {
            name: string | null;
            image: string | null;
            id: string;
            email?: string | null;
            bio?: string | null;
            socialLinks?: PrismaJson.SocialLinks | null;
            organizationName: string | null;
            organizationImage: string | null;
            organizationLink: string | null;
        };
    }[];
};
